// @ts-check
// Projeções de sub-campo (CEP na família, pacote 1.12.0, contrato 07, seção 9.9): a linha "CEP" da família e as
// colunas CEP, Bairro e Cidade da tabela são o próprio campo de endereço visto por uma parte, não um campo novo. A
// projeção herda tipo, nome e permissões do campo (o papel já tirou o campo que não lê e marcou isUIReadOnly no que não
// edita) e lê record[field.name]; o PATCH leva só a parte trocada, e o merge do motor completa o resto com o valor do
// banco (a tela pode estar até 15 s atrás de outra pessoa que mudou outra parte). Módulo puro.
import {
  DEFAULT_ADDRESS_COUNTRY, comparablePlaceText, followPostcodePlace, formatPostcodeForStorage, formatPostcodePlace, maskPostcode, readPostcodeDigits,
} from '../../../shared/address/postcode-autofill.js';
import { pickLeadPostcode } from '../../../shared/geo/lead-postcode.js';

/**
 * @typedef {import('./field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {{ apiPatch: Record<string, unknown>, localPatch: Record<string, unknown> } | null} FieldPatch
 * @typedef {import('../../../shared/address/postcode-autofill.js').AddressDraft} AddressDraft
 * @typedef {import('../../../shared/address/postcode-autofill.js').PostcodeAddress} PostcodeAddress
 */

export const POSTCODE_SUB_FIELD_NAME = 'addressPostcode';

// objeto -> campo de endereço cujo CEP vira uma linha própria na página e no painel do registro
export const RECORD_PAGE_POSTCODE_ROWS = Object.freeze({ household: 'householdAddress' });

const SUB_FIELD_ICONS = Object.freeze({ addressPostcode: 'IconMapPin' });

/**
 * Campo derivado que mostra e edita só uma parte do endereço. O id é único (chaves, edição e largura); o nome continua
 * o do campo, para record[field.name] e o PATCH apontarem para o campo certo.
 * @param {FieldMetadata} field @param {string} subFieldName
 * @param {{ withPlace?: boolean }} [options] withPlace: mostra o lugar do CEP (bairro, cidade e UF) ao lado
 * @returns {FieldMetadata}
 */
export function projectSubField(field, subFieldName, { withPlace = false } = {}) {
  return {
    ...field,
    id: `${field.id}.${subFieldName}`,
    sourceFieldId: field.id,
    sourceLabel: field.label,
    subFieldName,
    label: `familyPostcode.subFields.${subFieldName}`,
    icon: SUB_FIELD_ICONS[/** @type {keyof typeof SUB_FIELD_ICONS} */ (subFieldName)] ?? field.icon,
    withPlace,
  };
}

/** @param {FieldMetadata | null | undefined} field */
export const isSubFieldProjection = (field) => Boolean(field?.sourceFieldId && field.subFieldName);

/** @param {FieldMetadata | null | undefined} field */
export const isPostcodeProjection = (field) => isSubFieldProjection(field) && field?.subFieldName === POSTCODE_SUB_FIELD_NAME;

/** Id do campo de verdade por trás de uma coluna ou linha. @param {FieldMetadata} field */
export const sourceFieldIdOf = (field) => field.sourceFieldId ?? field.id;

/** @param {Record<string, any> | null | undefined} record @param {FieldMetadata} field */
export function readSubFieldValue(record, field) {
  const value = record?.[field.name]?.[/** @type {string} */ (field.subFieldName)];
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Patch de uma parte do endereço; null quando nada muda. A API recebe só a parte (e o país Brasil quando ele está
 * vazio), para não desfazer a correção que outra pessoa fez em outra parte; a tela troca a parte no endereço que mostra.
 * O CEP vai na forma guardada (00000-000).
 * @param {FieldMetadata} field @param {Record<string, any> | null | undefined} record @param {unknown} text
 * @returns {FieldPatch}
 */
export function buildSubFieldPatch(field, record, text) {
  const subFieldName = /** @type {string} */ (field.subFieldName);
  const current = record?.[field.name] ?? {};
  const country = String(current.addressCountry ?? '').trim();
  const trimmed = String(text ?? '').trim();
  const value = subFieldName === POSTCODE_SUB_FIELD_NAME ? formatPostcodeForStorage(trimmed, country) : trimmed;
  if (value === String(current[subFieldName] ?? '').trim()) return null;
  return buildAddressPartsPatch(field, current, { [subFieldName]: value });
}

/**
 * Patch com algumas partes do endereço: a API recebe só elas (mais o país Brasil quando vazio e alguma parte foi
 * preenchida); a tela, o endereço inteiro com elas trocadas. null quando nada muda.
 * @param {FieldMetadata} field @param {Record<string, any> | null | undefined} current endereço gravado
 * @param {Record<string, string>} parts
 * @returns {FieldPatch}
 */
export function buildAddressPartsPatch(field, current, parts) {
  const changed = Object.fromEntries(Object.entries(parts).filter(([name, value]) => String(current?.[name] ?? '').trim() !== value));
  if (Object.keys(changed).length === 0) return null;
  const hasValue = Object.values(changed).some((value) => value !== '');
  const country = String(current?.addressCountry ?? '').trim();
  const apiValue = { ...changed, ...(hasValue && !country ? { addressCountry: DEFAULT_ADDRESS_COUNTRY } : {}) };
  return { apiPatch: { [field.name]: apiValue }, localPatch: { [field.name]: { ...(current ?? {}), ...apiValue } } };
}

/**
 * Mesma rua, sem olhar o número e o que vem depois da vírgula: "Rua Padre José Gaspar, 52" é a "Rua Padre José Gaspar".
 * @param {unknown} saved @param {unknown} lookedUp
 */
export function isSameStreet(saved, lookedUp) {
  const base = (/** @type {unknown} */ value) => comparablePlaceText(String(value ?? '').split(',')[0]).replace(/\s+(n[º°o.]*\s*)?\d+[a-z]?$/, '').trim();
  const savedBase = base(saved);
  const lookedUpBase = base(lookedUp);
  return savedBase !== '' && savedBase === lookedUpBase;
}

/**
 * @typedef {{ next: AddressDraft, changes: Record<string, string>, keptStreet: string | null, keptNeighborhood: string | null }} PostcodeEditPlan
 *   next: o endereço que fica gravado; changes: só as partes que mudam; keptStreet e keptNeighborhood: o que ficou e
 *   pode ser de outro lugar (a tela avisa)
 */

/**
 * O que o editor só do CEP grava com a consulta do CEP digitado (contrato 07, seção 9.9.2). O editor não mostra a rua
 * nem o bairro, então a regra é a do CEP que chega de fora (followPostcodePlace) mais o que veio do CEP gravado: cidade
 * e UF seguem o CEP; o bairro segue quando não combina e sai quando era o do CEP gravado (ou de outra cidade) e o CEP
 * novo não traz bairro; a rua troca quando está vazia ou era a do CEP gravado, e fica quando a pessoa escreveu outra
 * (com o número). Sem a consulta do CEP gravado (serviço fora do ar), só a regra do CEP.
 * @param {AddressDraft | null | undefined} saved endereço gravado
 * @param {PostcodeAddress} address consulta do CEP digitado
 * @param {PostcodeAddress | null} savedAddress consulta do CEP gravado
 * @returns {PostcodeEditPlan}
 */
export function planPostcodeEdit(saved, address, savedAddress) {
  const current = saved ?? {};
  const text = (/** @type {unknown} */ value) => String(value ?? '').trim();
  const cameFromSavedPostcode = (/** @type {string} */ value, /** @type {unknown} */ lookedUp) => savedAddress !== null && value !== ''
    && text(lookedUp) !== '' && comparablePlaceText(value) === comparablePlaceText(lookedUp);
  const street = text(current.addressStreet1);
  const complement = text(current.addressStreet2);
  const isSavedNeighborhood = cameFromSavedPostcode(complement, savedAddress?.neighborhood);
  const changes = followPostcodePlace(isSavedNeighborhood ? { ...current, addressStreet2: '' } : current, {
    postcode: address.postcode, neighborhood: address.neighborhood, city: address.city, state: address.state,
  });
  if (isSavedNeighborhood && !('addressStreet2' in changes)) changes.addressStreet2 = '';
  const isSavedStreet = cameFromSavedPostcode(street, savedAddress?.street);
  if (street === '' || isSavedStreet) changes.addressStreet1 = text(address.street);
  const changed = Object.fromEntries(Object.entries(changes).filter(([name, value]) => text(current[name]) !== value));
  const next = { ...current, ...changed };
  const isOtherCity = text(current.addressCity) !== '' && text(address.city) !== '' && comparablePlaceText(current.addressCity) !== comparablePlaceText(address.city);
  const keptStreet = street !== '' && !('addressStreet1' in changes)
    && (text(address.street) !== '' ? !isSameStreet(street, address.street) : isOtherCity) ? street : null;
  // CEP sem bairro (o da cidade inteira) não confirma o bairro que ficou
  const keptNeighborhood = complement !== '' && !('addressStreet2' in changes) && text(address.neighborhood) === '' ? complement : null;
  return { next, changes: changed, keptStreet, keptNeighborhood };
}

/**
 * Texto curto do endereço que vai ficar gravado: "Av. José de Sousa Campos · Cambuí · Campinas/SP".
 * @param {AddressDraft | null | undefined} draft
 */
export function formatAddressPreview(draft) {
  const part = (/** @type {unknown} */ value) => String(value ?? '').trim();
  const cityAndState = [part(draft?.addressCity), part(draft?.addressState)].filter(Boolean).join('/');
  return [part(draft?.addressStreet1), part(draft?.addressStreet2), cityAndState].filter(Boolean).join(' · ');
}

/**
 * O que o editor só do CEP faz com o texto: CLEAR (vazio limpa só o CEP), INCOMPLETE (sem 8 dígitos: não grava; Enter
 * e Tab mantêm o editor aberto com o aviso, clique fora e Escape voltam ao gravado), UNCHANGED (o CEP gravado) ou SAVE.
 * @param {unknown} text @param {Record<string, any> | null | undefined} saved
 * @returns {'CLEAR' | 'INCOMPLETE' | 'UNCHANGED' | 'SAVE'}
 */
export function postcodeInputAction(text, saved) {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return 'CLEAR';
  const digits = readPostcodeDigits(trimmed);
  if (!digits) return trimmed === String(saved?.addressPostcode ?? '').trim() ? 'UNCHANGED' : 'INCOMPLETE';
  return digits === readPostcodeDigits(saved?.addressPostcode) ? 'UNCHANGED' : 'SAVE';
}

/** Delete na célula ou CEP apagado: limpa só aquela parte. @param {FieldMetadata} field @param {Record<string, any> | null | undefined} record */
export function buildSubFieldClearPatch(field, record) {
  return readSubFieldValue(record, field).trim() === '' ? null : buildSubFieldPatch(field, record, '');
}

/**
 * Campos da página do registro com a linha "CEP" logo antes do campo de endereço, só se ele estiver nos metadados
 * (já filtrados pelo papel).
 * @param {ObjectMetadata} objectMetadata @param {FieldMetadata[]} fields
 * @returns {FieldMetadata[]}
 */
export function withPostcodeRows(objectMetadata, fields) {
  const fieldName = RECORD_PAGE_POSTCODE_ROWS[/** @type {keyof typeof RECORD_PAGE_POSTCODE_ROWS} */ (objectMetadata.nameSingular)];
  if (!fieldName) return fields;
  const index = fields.findIndex((field) => field.name === fieldName && field.type === 'ADDRESS');
  if (index < 0) return fields;
  return [...fields.slice(0, index), projectSubField(fields[index], POSTCODE_SUB_FIELD_NAME, { withPlace: true }), ...fields.slice(index)];
}

/**
 * Texto curto do que o CEP vai preencher: "Av. José de Sousa Campos · Cambuí · Campinas/SP".
 * @param {{ street?: string, neighborhood?: string, city?: string, state?: string } | null | undefined} address
 */
export function formatPostcodePreview(address) {
  const cityAndState = [address?.city, address?.state].filter(Boolean).join('/');
  return [address?.street, address?.neighborhood, cityAndState].filter(Boolean).join(' · ');
}

/**
 * @typedef {{ state: 'HOUSEHOLD' | 'MALFORMED' | 'NOT_FOUND' | 'HOUSEHOLD_WITHOUT_POSTCODE' | 'WITHOUT_HOUSEHOLD',
 *   textKey: string, textValues: Record<string, string>, text: string | null, linkKey: string | null, tone: 'default' | 'warning' }} LeadPostcodeDescription
 *   text: o próprio CEP quando a família tem CEP sem lugar conhecido (dado, sem tradução)
 */

/** @param {string} raw @param {string | null} digits */
const displayPostcode = (raw, digits) => (digits ? maskPostcode(digits) : raw.trim());

/**
 * Indicação do CEP no card: o CEP da família que o mapa usa e onde corrigir, pela mesma regra do relatório
 * (pickLeadPostcode, só a família desde a 1.12.1). Família sem CEP pede para informar na família.
 * @param {{ household: Record<string, any> | null | undefined, householdPostcodeStatus?: 'FOUND' | 'NOT_FOUND' | null }} input
 *   household: a família do card (null sem família); householdPostcodeStatus: o que a escola sabe do CEP da família
 *   (não encontrado: o lugar gravado não é o dele)
 * @returns {LeadPostcodeDescription}
 */
export function describeLeadPostcode({ household, householdPostcodeStatus = null }) {
  const householdAddress = household?.householdAddress ?? null;
  const picked = pickLeadPostcode(householdAddress?.addressPostcode);
  const prefix = 'familyPostcode.cardHint';
  if (picked.source === 'HOUSEHOLD') {
    const raw = /** @type {string} */ (picked.raw);
    if (!picked.digits) {
      return { state: 'MALFORMED', textKey: `${prefix}.malformed`, textValues: {}, text: null, linkKey: `${prefix}.fixInHousehold`, tone: 'warning' };
    }
    const postcode = displayPostcode(raw, picked.digits);
    if (householdPostcodeStatus === 'NOT_FOUND') {
      return { state: 'NOT_FOUND', textKey: `${prefix}.notFound`, textValues: { postcode }, text: null, linkKey: `${prefix}.fixInHousehold`, tone: 'warning' };
    }
    const place = formatPostcodePlace(householdAddress);
    return {
      state: 'HOUSEHOLD', textKey: `${prefix}.value`, textValues: { postcode, place }, text: place ? null : postcode,
      linkKey: `${prefix}.openHousehold`, tone: 'default',
    };
  }
  return household
    ? { state: 'HOUSEHOLD_WITHOUT_POSTCODE', textKey: `${prefix}.householdWithoutPostcode`, textValues: {}, text: null, linkKey: `${prefix}.fillInHousehold`, tone: 'warning' }
    : { state: 'WITHOUT_HOUSEHOLD', textKey: `${prefix}.withoutHousehold`, textValues: {}, text: null, linkKey: null, tone: 'warning' };
}

const ADDRESS_TEXT_PARTS = Object.freeze(['addressStreet1', 'addressStreet2', 'addressCity', 'addressState', 'addressPostcode']);

/**
 * Endereço do formulário de criação para a API: tudo vazio (o país não conta) não vai; senão vai com o país Brasil
 * quando vazio e o CEP de 8 dígitos já na forma guardada.
 * @param {Record<string, any> | null | undefined} value
 * @returns {Record<string, any> | undefined}
 */
export function toAddressApiValue(value) {
  if (!value || typeof value !== 'object') return undefined;
  if (!ADDRESS_TEXT_PARTS.some((name) => String(value[name] ?? '').trim() !== '')) return undefined;
  const addressCountry = String(value.addressCountry ?? '').trim() || DEFAULT_ADDRESS_COUNTRY;
  return { ...value, addressCountry, addressPostcode: formatPostcodeForStorage(value.addressPostcode, addressCountry) };
}

/**
 * Onde a indicação do CEP entra nos campos do card: logo depois da Etapa (a primeira tela do card, sem rolar), ou no
 * topo quando a Etapa não está na lista. Só no card.
 * @param {string} objectNameSingular @param {string[]} fieldNames campos da página, na ordem
 * @returns {{ isShown: boolean, afterFieldName: string | null }}
 */
export function familyPostcodeHintSlot(objectNameSingular, fieldNames) {
  if (objectNameSingular !== 'enrollmentOpportunity') return { isShown: false, afterFieldName: null };
  return { isShown: true, afterFieldName: fieldNames.includes('stage') ? 'stage' : null };
}

/**
 * Texto com o CEP separado, para o CEP não quebrar no hífen (o resto do texto quebra normalmente).
 * @param {string} text @param {string | null | undefined} postcode
 * @returns {Array<{ text: string, isPostcode: boolean }>}
 */
export function splitAroundPostcode(text, postcode) {
  if (!postcode || !text.includes(postcode)) return [{ text, isPostcode: false }];
  return text.split(postcode).flatMap((part, index) => [
    ...(index > 0 ? [{ text: postcode, isPostcode: true }] : []),
    ...(part ? [{ text: part, isPostcode: false }] : []),
  ]);
}

/** Dígitos do CEP de um endereço gravado, para saber se a consulta ainda vale para ele. @param {Record<string, any> | null | undefined} address */
export const postcodeDigitsOf = (address) => readPostcodeDigits(address?.addressPostcode);
